import { ColDef, RowSelectedEvent } from '@ag-grid-community/core';
import { Button, Flex, Icon, useDisclosure } from '@chakra-ui/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionMenu } from '../../../components/action-menu';
import { PhysicianModal } from '../../../components/modal/physician';
import { PhysicianDto, api } from '../../../lib/api';
import { Tag } from '@/components/ui/tag';
import { SquarePen } from 'lucide-react';
import { Grid } from '@/components/ui/grid';

export const PhysiciansPage = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { open: isPhysicianModalOpen, onOpen: onPhysicianModalOpen, onClose: onPhysicianModalClose } = useDisclosure();

    const query = useQuery<PhysicianDto[]>({
        queryKey: ['physician', 'list'],
        queryFn: async () => {
            const { data } = await api.physician.list();
            return data;
        },
    });

    const [colDefs] = useState<ColDef<PhysicianDto | { actions: any }>[]>([
        { field: 'npi', headerName: 'NPI', flex: 1, filter: 'agTextColumnFilter', suppressHeaderFilterButton: false, resizable: false },
        { field: 'firstName', flex: 1.5, filter: 'agTextColumnFilter', suppressHeaderFilterButton: false, resizable: true, sort: 'asc' },
        { field: 'lastName', flex: 1.5, filter: 'agTextColumnFilter', suppressHeaderFilterButton: false, resizable: false },
        {
            field: 'credential',
            headerName: 'Credentials',
            flex: 1,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: true,
            resizable: false,
        },
        {
            field: 'pecos',
            headerName: 'PECOS',
            flex: 1,
            filter: 'agTextColumnFilter',
            suppressHeaderFilterButton: false,
            resizable: false,
            cellRenderer: ({ data }: { data: PhysicianDto }) => {
                return (
                    <Flex alignItems="center" height="100%">
                        <Tag colorScheme={data.pecos ? 'green' : 'red'} size="sm" textTransform="uppercase" fontWeight="700">
                            {data.pecos ? 'enrolled' : 'not enrolled'}
                        </Tag>
                    </Flex>
                );
            },
        },
        { field: 'city', flex: 1, filter: 'agTextColumnFilter', suppressHeaderFilterButton: true, resizable: false },
        { field: 'phone', flex: 1.2, filter: 'agTextColumnFilter', suppressHeaderFilterButton: true, resizable: false },
        { field: 'fax', flex: 1.2, filter: 'agTextColumnFilter', suppressHeaderFilterButton: true, resizable: false },
        {
            flex: 1,
            suppressHeaderFilterButton: true,
            resizable: false,
            cellRenderer: () => {
                return (
                    <ActionMenu
                        actions={[
                            {
                                id: 'primary',
                                action: () => null,
                                icon: (
                                    <Icon boxSize={5}>
                                        <SquarePen />
                                    </Icon>
                                ),
                                label: 'Edit',
                            },
                        ]}
                    />
                );
            },
        },
    ]);

    const handleAdd = async () => {
        await queryClient.invalidateQueries({ queryKey: ['physician', 'list'] });
        onPhysicianModalClose();
    };

    const handleSelect = (event: RowSelectedEvent) => {
        if (event.node.isSelected()) {
            navigate(`/settings/physicians/${event.node.data.id}/edit`);
        }
    };

    return (
        <>
            <Flex h="40px" alignItems="center" justifyContent="space-between" pb="12px">
                <Flex>
                    <Button size="sm" onClick={() => onPhysicianModalOpen()} textDecoration="none">
                        Add Physician
                    </Button>
                </Flex>
            </Flex>
            <Flex flexDirection="column" style={{ width: '100%', height: 'calc(100% - 40px)' }}>
                <Grid
                    rowSelection="single"
                    suppressRowHoverHighlight
                    suppressCellFocus
                    columnDefs={colDefs}
                    rowData={query.data}
                    onRowSelected={handleSelect}
                />
            </Flex>

            <PhysicianModal isOpen={isPhysicianModalOpen} onClose={onPhysicianModalClose} onAdd={() => handleAdd()} />
        </>
    );
};
